import { useState, useRef, useEffect, useMemo } from 'react'
import styles from './DayView.module.css'
import CalendarWorklogModal from './modals/CalendarWorklogModal'
import WorklogContextMenu from './WorklogContextMenu'
import WorklogDescription from './WorklogDescription'
import useWorklogDragAndDrop from '../hooks/useWorklogDragAndDrop'
// Overnight handling removed - backend now splits worklogs at creation/update time
import { moment, formatHourMinute12Hour, isToday, isSameDay, formatDuration, getSlotHeight } from '../utils/dateUtils'
import { formatTime12Hour } from '../utils/dateUtilsV2'
import { getUserTimezone } from '../utils/timezoneUtils'

export default function DayView({
    currentDate,
    selectedDate,
    worklogs,
    calendarEvents = [],
    onWorklogCreate,
    onWorklogUpdate,
    onWorklogDelete,
    onSuggestionAccept = () => { },
    onSuggestionDelete = () => { },
    selectedTeammates = [],
    teammates = [],
    getWorklogColors,
    isAdmin = false,
    currentUser = null,
    user = null, // Add user prop for timezone
    availableProjects = [],
    availableTasks = [],
    apiToken = null,
    viewDensity = 'relaxed',
    setIsLoading = () => { },
    setLoadingMessage = () => { }
}) {
    // Get user's timezone for consistent time display - prioritize user profile timezone
    // Use useMemo to recalculate when user.timezone changes
    const userTimezone = useMemo(() => getUserTimezone(user?.timezone), [user?.timezone])

    const [showModal, setShowModal] = useState(false)
    const [pendingWorklog, setPendingWorklog] = useState(null)
    const [isEditMode, setIsEditMode] = useState(false)
    const [hoveredWorklog, setHoveredWorklog] = useState(null)
    const currentTimeLineRef = useRef(null)

    // Context menu state
    const [contextMenu, setContextMenu] = useState({
        isVisible: false,
        position: { x: 0, y: 0 },
        worklog: null
    })

    const gridRef = useRef(null)

    const mentionableUsers = useMemo(() => {
        const list = [...teammates]
        if (currentUser) {
            list.push(currentUser)
        }
        return list
    }, [teammates, currentUser])

    // Use the drag and drop hook
    const {
        dragStart,
        isDragging,
        dragPreview,
        draggedWorklog,
        isDraggingWorklog,
        isAltPressed,
        isShiftPressed,
        isResizing,
        resizeWorklog,
        resizeType,
        handleMouseDown,
        handleWorklogMouseDown,
        handleDoubleClick,
        handleResizeMouseDown,
        getDragPreviewStyle,
        getDragPreviewStyleForExistingWorklog,
        getResizePreviewStyle
    } = useWorklogDragAndDrop(onWorklogUpdate, styles, {
        onNewWorklogDrop: (worklogData) => {
            setPendingWorklog(worklogData)
            setIsEditMode(false)
            setShowModal(true)
        },
        onDoubleClick: (worklogData) => {
            setPendingWorklog(worklogData)
            setIsEditMode(false)
            setShowModal(true)
        },
        onWorklogDuplicate: (worklogData) => {
            // Create a duplicate worklog
            onWorklogCreate(worklogData)
        },
        onWorklogMoveStart: () => {
            setIsLoading(true)
            setLoadingMessage('Moving worklog...')
        },
        onWorklogMoveEnd: () => {
            setIsLoading(false)
        }
    }, currentUser, viewDensity, userTimezone)

    // Context menu handlers
    const handleContextMenu = (event, worklog) => {
        event.preventDefault()
        event.stopPropagation()

        setContextMenu({
            isVisible: true,
            position: { x: event.clientX, y: event.clientY },
            worklog: worklog
        })
    }

    const closeContextMenu = () => {
        setContextMenu({
            isVisible: false,
            position: { x: 0, y: 0 },
            worklog: null
        })
    }

    const handleDuplicate = () => {
        const worklog = contextMenu.worklog
        if (!worklog) return

        const duplicateData = {
            title: worklog.title,
            startTime: worklog.startTime,
            endTime: worklog.endTime,
            date: worklog.date,
            project: worklog.project?.id || worklog.project,
            task: worklog.task?.id || worklog.task,
            userId: worklog.userId,
            billable: worklog.billable,
            type: 'work'
        }

        onWorklogCreate(duplicateData)
    }

    const handleDuplicateForMe = () => {
        const worklog = contextMenu.worklog
        console.log('=== DayView handleDuplicateForMe DEBUG ===')
        console.log('worklog:', worklog)
        console.log('currentUser:', currentUser)
        console.log('currentUser.id:', currentUser?.id)

        if (!worklog || !currentUser?.id) {
            console.log('Early return - missing worklog or currentUser.id')
            return
        }

        const duplicateData = {
            title: worklog.title,
            startTime: worklog.startTime,
            endTime: worklog.endTime,
            date: worklog.date,
            project: worklog.project?.id || worklog.project,
            task: worklog.task?.id || worklog.task,
            userId: currentUser.id,
            billable: worklog.billable,
            type: 'work'
        }

        console.log('duplicateData being sent:', duplicateData)
        console.log('Original worklog userId:', worklog.userId)
        console.log('New worklog userId:', duplicateData.userId)

        onWorklogCreate(duplicateData)
    }

    const handleSplitFromMenu = () => {
        const worklog = contextMenu.worklog
        if (!worklog) return

        handleWorklogSplit(worklog)
    }

    const handleDeleteFromMenu = () => {
        const worklog = contextMenu.worklog
        if (!worklog) return

        handleWorklogDelete(worklog)
    }

    // Handle worklog deletion
    const handleWorklogDelete = async (worklog) => {
        const startTime = formatTime12Hour(worklog.startTime, userTimezone)
        const endTime = formatTime12Hour(worklog.endTime, userTimezone)
        const confirmMessage = `Are you sure you want to delete this worklog?\n\n"${worklog.title}"\n${startTime} - ${endTime}`

        if (window.confirm(confirmMessage)) {
            try {
                await onWorklogDelete(worklog.id)
                setHoveredWorklog(null) // Clear hover state after deletion
            } catch (error) {
                console.error('Failed to delete worklog:', error)
                alert('Failed to delete worklog: ' + error.message)
            }
        }
    }

    // Handle worklog splitting (Ctrl+click)
    const handleWorklogSplit = async (worklog) => {
        try {
            // Calculate the midpoint time - this ensures first entry's end time = second entry's start time
            const startTime = moment(worklog.startTime)
            const endTime = moment(worklog.endTime)
            const totalDuration = endTime.diff(startTime)
            const halfDuration = totalDuration / 2
            const midTime = moment(startTime).add(halfDuration, 'milliseconds')

            // First half - update the existing worklog (original start → midpoint)
            const firstHalfData = {
                start_time: startTime.toISOString(),
                end_time: midTime.toISOString(), // First entry ends at midTime
                description: worklog.title,
                project: worklog.project?.id || worklog.project,
                task: worklog.task?.id || worklog.task,
                user_id: worklog.userId, // Preserve original user assignment
                billable: worklog.billable
            }

            // Second half - create a new worklog (midpoint → original end)
            const secondHalfData = {
                title: worklog.title,
                startTime: midTime.toDate(), // Second entry starts at midTime (same as first entry's end)
                endTime: endTime.toDate(),
                date: worklog.date,
                project: worklog.project?.id || worklog.project,
                task: worklog.task?.id || worklog.task,
                userId: worklog.userId,
                billable: worklog.billable,
                type: 'work'
            }

            // Execute operations sequentially to avoid state update conflicts
            console.log('=== Starting sequential operations ===')
            const updateResult = await onWorklogUpdate(worklog.id, firstHalfData)
            console.log('=== Update completed, now creating ===')
            const createResult = await onWorklogCreate(secondHalfData)
            console.log('=== Create completed ===')

            // Small delay to ensure state is fully processed
            await new Promise(resolve => setTimeout(resolve, 50))

            console.log('Worklog split completed successfully:', { updateResult, createResult })

            // Force a small delay to ensure state updates are processed
            await new Promise(resolve => setTimeout(resolve, 100))

        } catch (error) {
            console.error('Failed to split worklog:', error)
            alert('Failed to split worklog: ' + error.message)
        }
    }

    // Generate time slots (24 hours in 15-minute intervals)
    const generateTimeSlots = () => {
        const slots = []
        for (let hour = 0; hour < 24; hour++) {
            for (let minute = 0; minute < 60; minute += 15) {
                const time = `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`
                slots.push({ hour, minute, time })
            }
        }
        return slots
    }

    const timeSlots = generateTimeSlots()

    // Handle keyboard events for delete functionality
    useEffect(() => {
        const handleKeyDown = (e) => {
            if ((e.key === 'Delete' || e.key === 'Backspace') && hoveredWorklog) {
                handleWorklogDelete(hoveredWorklog)
            }
        }

        document.addEventListener('keydown', handleKeyDown)
        return () => document.removeEventListener('keydown', handleKeyDown)
    }, [hoveredWorklog])

    // Update current time indicator position every minute via direct DOM manipulation
    // Uses a ref instead of state to avoid re-rendering the entire component
    useEffect(() => {
        const updateTimeLinePosition = () => {
            if (!currentTimeLineRef.current) return
            const now = moment.tz(moment(), userTimezone)
            const currentMinutes = now.hour() * 60 + now.minute()
            const slotHeight = getSlotHeight(viewDensity)
            const top = (currentMinutes / 15) * slotHeight
            currentTimeLineRef.current.style.top = `${top}px`
        }

        updateTimeLinePosition()
        const interval = setInterval(updateTimeLinePosition, 60000)

        return () => clearInterval(interval)
    }, [userTimezone, viewDensity])

    // Scroll to first worklog or 8:00am on component mount and when date changes
    useEffect(() => {
        const scrollToOptimalPosition = () => {
            if (gridRef.current) {
                const slotHeight = getSlotHeight(viewDensity)
                let scrollPosition = 0

                // Find worklogs for the selected day
                const dayWorklogs = worklogs.filter(worklog => {
                    const worklogDate = moment(worklog.date || worklog.startTime).startOf('day')
                    const selectedDayStart = moment(selectedDate).startOf('day')
                    return worklogDate.isSame(selectedDayStart, 'day')
                })

                console.log('DayView: dayWorklogs count:', dayWorklogs.length, 'for date:', moment(selectedDate).format('YYYY-MM-DD'))

                if (dayWorklogs && dayWorklogs.length > 0) {
                    // Get all start times in minutes from midnight
                    const startTimes = dayWorklogs.map(worklog => {
                        const startTime = moment(worklog.startTime)
                        const minutes = startTime.hour() * 60 + startTime.minute()
                        console.log(`DayView: worklog at ${startTime.format('HH:mm')} (${minutes} minutes)`)
                        return minutes
                    })

                    if (startTimes.length > 0) {
                        // Find the earliest start time
                        const earliestMinutes = Math.min(...startTimes)
                        console.log('DayView: Earliest worklog time:', earliestMinutes, 'minutes from midnight')

                        // Add padding (scroll up a bit before the first entry)
                        const paddingMinutes = 30 // Show 30 minutes before first entry
                        const targetMinutes = Math.max(0, earliestMinutes - paddingMinutes)
                        console.log('DayView: Target scroll time (with padding):', targetMinutes, 'minutes from midnight')

                        // Calculate scroll position (4 slots per hour, each slot is slotHeight)
                        scrollPosition = (targetMinutes / 15) * slotHeight - 10
                        console.log('DayView: Calculated scroll position:', scrollPosition, 'pixels')
                    } else {
                        // Fallback to 8:00am if no worklogs
                        console.log('DayView: No start times, falling back to 8:00 AM')
                        const eightAMSlotIndex = 8 * 4
                        scrollPosition = eightAMSlotIndex * slotHeight - 5
                    }
                } else {
                    // Fallback to 8:00am if no worklogs
                    console.log('DayView: No worklogs, falling back to 8:00 AM')
                    const eightAMSlotIndex = 8 * 4
                    scrollPosition = eightAMSlotIndex * slotHeight - 5
                }

                // Find the scrollable container (timeGrid)
                const timeGrid = gridRef.current.querySelector(`.${styles.timeGrid}`)
                console.log('DayView: timeGrid element found:', !!timeGrid)
                if (timeGrid) {
                    const finalScrollPosition = Math.max(0, scrollPosition)
                    console.log('DayView: Setting scrollTop to:', finalScrollPosition)
                    timeGrid.scrollTop = finalScrollPosition
                }
            }
        }

        // Prevent browser scroll restoration from interfering
        if (window.history?.scrollRestoration) {
            window.history.scrollRestoration = 'manual'
        }

        // Schedule scroll after DOM is rendered
        const frameId = requestAnimationFrame(scrollToOptimalPosition)
        return () => cancelAnimationFrame(frameId)
    }, [selectedDate, viewDensity, worklogs])

    // Get user colors for worklog display
    const getUserColors = (userId) => {
        if (userId === currentUser?.id) {
            return getWorklogColors('self')
        }
        return getWorklogColors(userId)
    }

    // Helper function to get teammate display name
    const getTeammateDisplayName = (userId) => {
        if (userId === currentUser?.id) return null // Don't show name for current user
        const teammate = teammates.find(t => t.id === userId)
        return teammate?.display_name || teammate?.first_name || 'Unknown User'
    }



    // Detect overlapping worklogs and calculate positioning
    const getOverlapInfo = (targetWorklog, dayWorklogs) => {
        // Extract hour/minute in user's timezone for correct overlap detection
        const targetStart = moment.tz(targetWorklog.startTime, userTimezone).hour() * 60 + moment.tz(targetWorklog.startTime, userTimezone).minute()
        const targetEnd = moment.tz(targetWorklog.endTime, userTimezone).hour() * 60 + moment.tz(targetWorklog.endTime, userTimezone).minute()

        // Find all overlapping worklogs (including the target)
        const overlapping = dayWorklogs.filter(worklog => {
            const start = moment.tz(worklog.startTime, userTimezone).hour() * 60 + moment.tz(worklog.startTime, userTimezone).minute()
            const end = moment.tz(worklog.endTime, userTimezone).hour() * 60 + moment.tz(worklog.endTime, userTimezone).minute()

            // Check if there's any time overlap
            return start < targetEnd && end > targetStart
        })

        // Sort by start time, then by id for consistent positioning
        overlapping.sort((a, b) => {
            const aStart = moment.tz(a.startTime, userTimezone).hour() * 60 + moment.tz(a.startTime, userTimezone).minute()
            const bStart = moment.tz(b.startTime, userTimezone).hour() * 60 + moment.tz(b.startTime, userTimezone).minute()
            if (aStart !== bStart) return aStart - bStart
            return a.id - b.id
        })

        const totalColumns = overlapping.length
        const columnIndex = overlapping.findIndex(w => w.id === targetWorklog.id)

        return { totalColumns, columnIndex }
    }

    const getWorklogStyle = (worklog, dayWorklogs) => {
        // Extract hour/minute in user's timezone for correct grid positioning
        const startMinutes = moment.tz(worklog.startTime, userTimezone).hour() * 60 + moment.tz(worklog.startTime, userTimezone).minute()
        const endMinutes = moment.tz(worklog.endTime, userTimezone).hour() * 60 + moment.tz(worklog.endTime, userTimezone).minute()

        // Each time slot is 15 minutes, grid row height is calculated accordingly
        const slotHeight = getSlotHeight(viewDensity)
        const maxMinutes = 24 * 60 // 1440 minutes in a day

        // Cap the end time to end of day to prevent overflow
        const cappedEndMinutes = Math.min(endMinutes, maxMinutes)
        const duration = cappedEndMinutes - startMinutes

        const top = (startMinutes / 15) * slotHeight
        const height = (duration / 15) * slotHeight

        // Calculate overlap positioning
        const { totalColumns, columnIndex } = getOverlapInfo(worklog, dayWorklogs)

        // Calculate width and position based on overlaps
        const availableWidth = 100 // percentage
        const columnWidth = availableWidth / totalColumns
        const leftPercent = columnIndex * columnWidth

        // Get colors based on user
        const worklogColors = getUserColors(worklog.userId)

        // Use project color for border if available, otherwise fall back to user color
        const borderColor = worklog.project?.color || worklogColors.borderColor

        // Set border style based on billable status - dotted for non-billable, solid for billable
        const borderStyle = worklog.billable === false ? 'dotted' : 'solid'

        return {
            top: `${top}px`,
            height: `${height}px`,
            left: `${leftPercent}%`,
            width: `${columnWidth}%`,
            paddingLeft: '2px',
            paddingRight: '2px',
            backgroundColor: worklogColors.cardColor,
            borderLeft: `10px ${borderStyle} ${borderColor}`,
            color: worklogColors.textColor,
            filter: 'brightness(0.95)'
        }
    }


    // Using centralized formatTime12Hour from dateUtils


    // Check if current time indicator should be shown (only for today)
    const shouldShowCurrentTimeIndicator = () => {
        return isToday(currentDate)
    }

    // Calculate daily total for a specific date (only for visible teammates)
    const calculateDailyTotal = (date) => {
        // Filter by date first
        const dayWorklogs = worklogs.filter(worklog => isSameDay(worklog.date, date))

        // Then filter by selected teammates (same logic as in worklog display)
        const visibleDayWorklogs = dayWorklogs.filter(worklog => {
            const isCurrentUserWorklog = worklog.userId === currentUser?.id
            const isUserSelected = selectedTeammates.includes(worklog.userId)
            const isSelfSelected = selectedTeammates.includes('self') && isCurrentUserWorklog

            return isUserSelected || isSelfSelected
        })

        const totalMinutes = visibleDayWorklogs.reduce((total, worklog) => {
            const duration = moment(worklog.endTime).diff(moment(worklog.startTime), 'minutes')
            return total + duration
        }, 0)

        return formatDuration(totalMinutes * 60) // formatDuration expects seconds
    }

    const formatDateHeader = (date) => {
        const isTodayDate = isToday(date)
        const dailyTotal = calculateDailyTotal(date)

        return (
            <div className={`${styles.dateHeader} ${isTodayDate ? styles.today : ''}`}>
                <div className={styles.dayName}>
                    {moment(date).format('dddd')}
                </div>
                <div className={styles.dayNumber}>
                    {moment(date).format('D')}
                </div>
                {dailyTotal && (
                    <div className={styles.dailyTotal}>
                        {dailyTotal}
                    </div>
                )}
            </div>
        )
    }

    // Filter for the current day and selected teammates
    const dayWorklogs = worklogs.filter(worklog => {
        const isOnDate = isSameDay(worklog.date, currentDate)
        if (!isOnDate) return false

        // Show worklog if user is selected or if it's current user's worklog and 'self' is selected
        const isCurrentUserWorklog = worklog.userId === currentUser?.id
        const isUserSelected = selectedTeammates.includes(worklog.userId)
        const isSelfSelected = selectedTeammates.includes('self') && isCurrentUserWorklog

        return isUserSelected || isSelfSelected
    })

    const renderSuggestionBlock = (worklog, worklogStyle, shouldHideTime) => {
        const suggestedByName = worklog.suggestedBy?.name || worklog.suggestedBy?.username || 'Teammate'

        return (
            <div
                key={worklog.id}
                className={`${styles.worklog} ${styles.suggestion}`}
                style={worklogStyle}
            >
                <div className={styles.suggestionBadge}>
                    Suggested by {suggestedByName}
                </div>
                <WorklogDescription
                    description={worklog.title}
                    className={styles.worklogTitle}
                    mentionableUsers={mentionableUsers}
                />
                <div className={`${styles.worklogTime} ${shouldHideTime ? styles.hideTime : ''}`}>
                    {formatTime12Hour(worklog.startTime, userTimezone)} - {formatTime12Hour(worklog.endTime, userTimezone)}
                </div>
                {worklog.project?.name && (
                    <div className={styles.projectTaskInfo}>
                        {worklog.project.name}
                        {worklog.task && ` > ${worklog.task.name || 'Task'}`}
                    </div>
                )}
                <div className={styles.suggestionActions}>
                    <button
                        className={styles.suggestionAccept}
                        onClick={(event) => {
                            event.stopPropagation()
                            onSuggestionAccept(worklog)
                        }}
                        title="Accept suggestion"
                        aria-label="Accept suggestion"
                    >
                        ✓
                    </button>
                    <button
                        className={styles.suggestionDelete}
                        onClick={(event) => {
                            event.stopPropagation()
                            onSuggestionDelete(worklog)
                        }}
                        title="Decline suggestion"
                        aria-label="Decline suggestion"
                    >
                        ✗
                    </button>
                </div>
            </div>
        )
    }

    return (
        <div className={styles.dayView} ref={gridRef}>
            {/* Day header */}
            <div className={styles.dayHeader}>
                <div className={styles.timeColumn}>
                    {/* Empty space above time column */}
                </div>
                <div className={styles.dateColumn}>
                    {formatDateHeader(currentDate)}
                </div>
            </div>

            {/* Time grid */}
            <div
                className={styles.timeGrid}
                style={{
                    '--calendar-slot-height': viewDensity === 'relaxed' ? '20px' : '16px'
                }}
            >
                {/* Time labels column */}
                <div className={styles.timeLabels}>
                    {timeSlots.map((slot, index) => (
                        <div key={index} className={styles.timeSlot}>
                            {slot.minute === 0 && (
                                <span className={styles.timeLabel}>
                                    {formatHourMinute12Hour(slot.hour, slot.minute)}
                                </span>
                            )}
                        </div>
                    ))}
                </div>

                {/* Day column */}
                <div className={styles.dayColumn}>
                    {/* Time slots for this day */}
                    {timeSlots.map((slot, slotIndex) => (
                        <div
                            key={slotIndex}
                            className={styles.timeSlot}
                            data-slot={`${slot.hour}:${slot.minute}`}
                            data-date={moment(currentDate).toISOString()}
                            onMouseDown={(e) => handleMouseDown(e, currentDate, slot)}
                            onDoubleClick={(e) => handleDoubleClick(e, currentDate, slot)}
                        />
                    ))}

                    {/* Worklogs for this day */}
                    {dayWorklogs.map(worklog => {
                        // Hide the original worklog only if it's being moved (not duplicated)
                        const isBeingMoved = isDraggingWorklog && draggedWorklog?.id === worklog.id && !isAltPressed

                        if (isBeingMoved) {
                            return null // Don't render the worklog in its original position when moving
                        }

                        // For duplication mode, show original worklog in original position
                        const isBeingDuplicated = isDraggingWorklog && draggedWorklog?.id === worklog.id && isAltPressed

                        // Calculate worklog height in pixels
                        const worklogStyle = isBeingMoved ? getWorklogStyle(draggedWorklog, dayWorklogs) : getWorklogStyle(worklog, dayWorklogs)
                        const worklogHeight = parseFloat(worklogStyle.height)

                        // Determine which elements to hide based on height
                        const shouldHideFooter = worklogHeight < 72
                        const shouldHideTime = worklogHeight < 48

                        if (worklog.type === 'suggestion') {
                            return renderSuggestionBlock(worklog, worklogStyle, shouldHideTime)
                        }

                        return (
                            <div
                                key={worklog.id}
                                className={`${styles.worklog} ${styles[worklog.type]} ${isBeingMoved ? styles.dragging : ''} ${isBeingDuplicated ? styles.duplicating : ''}`}
                                style={worklogStyle}
                                onMouseDown={(e) => handleWorklogMouseDown(e, worklog)}
                                onMouseEnter={() => setHoveredWorklog(worklog)}
                                onMouseLeave={() => setHoveredWorklog(null)}
                                onContextMenu={(e) => handleContextMenu(e, worklog)}
                                onClick={(e) => {
                                    // Only handle click if not dragging
                                    if (!isDraggingWorklog) {
                                        if (e.altKey && e.shiftKey) {
                                            // Shift+Option+click - duplicate for me
                                            setContextMenu(prev => ({ ...prev, worklog }))
                                            handleDuplicateForMe()
                                        } else if (e.altKey && (e.ctrlKey || e.metaKey)) {
                                            // Option+Cmd+click - duplicate for me
                                            setContextMenu(prev => ({ ...prev, worklog }))
                                            handleDuplicateForMe()
                                        } else if (e.altKey) {
                                            // Option+click - duplicate the worklog
                                            setContextMenu(prev => ({ ...prev, worklog }))
                                            handleDuplicate()
                                        } else if (e.ctrlKey || e.metaKey) {
                                            // Ctrl+click (or Cmd+click on Mac) - split the worklog
                                            handleWorklogSplit(worklog)
                                        } else {
                                            // Normal click - edit the worklog
                                            setPendingWorklog(worklog)
                                            setIsEditMode(true)
                                            setShowModal(true)
                                        }
                                    }
                                }}
                            >
                                <WorklogDescription
                                    description={worklog.title}
                                    className={styles.worklogTitle}
                                    mentionableUsers={mentionableUsers}
                                />
                                {getTeammateDisplayName(worklog.userId) && (
                                    <div className={styles.worklogUser}>
                                        {getTeammateDisplayName(worklog.userId)}
                                    </div>
                                )}
                                <div className={`${styles.worklogTime} ${shouldHideTime ? styles.hideTime : ''}`}>
                                    {formatTime12Hour(worklog.startTime, userTimezone)} - {formatTime12Hour(worklog.endTime, userTimezone)}
                                </div>
                                <div className={`${styles.worklogFooter} ${shouldHideFooter ? styles.hideFooter : ''}`}>
                                    <div className={styles.projectTaskInfo}>
                                        {
                                            worklog.project?.name && (
                                                <span>
                                                    {worklog.project.name}
                                                    {worklog.task && ` > ${typeof worklog.task === 'object' ? (worklog.task.name || 'Task') : worklog.task}`}
                                                </span>
                                            )}
                                    </div>
                                </div>

                                {/* Resize handles */}
                                <div
                                    className={styles.resizeHandleTop}
                                    onMouseDown={(e) => handleResizeMouseDown(e, worklog, 'top')}
                                />
                                <div
                                    className={styles.resizeHandleBottom}
                                    onMouseDown={(e) => handleResizeMouseDown(e, worklog, 'bottom')}
                                />
                            </div>
                        )
                    }).filter(Boolean)}

                    {/* Calendar Events for this day */}
                    {calendarEvents
                        .filter(event => isSameDay(event.startTime, currentDate))
                        .map(event => {
                            const slotHeight = getSlotHeight(viewDensity)
                            const startMoment = moment(event.startTime)
                            const endMoment = moment(event.endTime)
                            const startMinutes = startMoment.hour() * 60 + startMoment.minute()
                            const durationMinutes = endMoment.diff(startMoment, 'minutes')

                            // Calculate position and height
                            const top = (startMinutes / 15) * slotHeight
                            const height = (durationMinutes / 15) * slotHeight

                            return (
                                <div
                                    key={event.id}
                                    className={styles.calendarEvent}
                                    style={{
                                        top: `${top}px`,
                                        height: `${height}px`,
                                        left: '4px',
                                        right: '4px',
                                        position: 'absolute',
                                        backgroundColor: '#E0F2FE',
                                        border: '1px solid #7DD3FC',
                                        borderRadius: '4px',
                                        padding: '4px 8px',
                                        fontSize: '12px',
                                        overflow: 'hidden',
                                        cursor: 'pointer',
                                        opacity: 0.7,
                                        zIndex: 0
                                    }}
                                    onClick={() => {
                                        // Create worklog from calendar event
                                        const worklogData = {
                                            title: event.description || event.summary,
                                            startTime: event.startTime,
                                            endTime: event.endTime,
                                            date: moment(event.startTime).startOf('day').toDate(),
                                            billable: true,
                                            type: 'work'
                                        }
                                        setPendingWorklog(worklogData)
                                        setIsEditMode(false)
                                        setShowModal(true)
                                    }}
                                    onMouseEnter={(e) => {
                                        e.currentTarget.style.opacity = '1'
                                        e.currentTarget.style.zIndex = '1'
                                    }}
                                    onMouseLeave={(e) => {
                                        e.currentTarget.style.opacity = '0.7'
                                        e.currentTarget.style.zIndex = '0'
                                    }}
                                >
                                    <div style={{ fontWeight: 500, color: '#0369A1' }}>
                                        {event.summary}
                                    </div>
                                    {height > 40 && (
                                        <div style={{ fontSize: '11px', color: '#0C4A6E', marginTop: '2px' }}>
                                            {formatTime12Hour(event.startTime, userTimezone)} - {formatTime12Hour(event.endTime, userTimezone)}
                                        </div>
                                    )}
                                </div>
                            )
                        })}

                    {/* Drag preview for new worklog creation */}
                    {isDragging && dragPreview && isSameDay(dragPreview.date, currentDate) && (
                        <div
                            className={`${styles.worklog} ${styles.preview}`}
                            style={getDragPreviewStyle()}
                        >
                            <div className={styles.worklogTitle}></div>
                            <div className={styles.worklogTime}>
                                {formatTime12Hour(dragPreview.startTime, userTimezone)} - {formatTime12Hour(dragPreview.endTime, userTimezone)}
                            </div>
                            <div className={styles.worklogFooter}>
                                <div className={styles.projectTaskInfo}>
                                    <span>Select project</span>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Drag preview for existing worklog being moved */}
                    {isDraggingWorklog && draggedWorklog && isSameDay(draggedWorklog.date, currentDate) && (
                        <div
                            className={`${styles.worklog} ${styles.preview} ${styles[draggedWorklog.type]}`}
                            style={getDragPreviewStyleForExistingWorklog()}
                        >
                            <WorklogDescription
                                description={draggedWorklog.title}
                                className={styles.worklogTitle}
                                mentionableUsers={mentionableUsers}
                            />
                            {getTeammateDisplayName(draggedWorklog.userId) && (
                                <div className={styles.worklogUser}>
                                    {getTeammateDisplayName(draggedWorklog.userId)}
                                </div>
                            )}
                            <div className={styles.worklogTime}>
                                {formatTime12Hour(draggedWorklog.startTime, userTimezone)} - {formatTime12Hour(draggedWorklog.endTime, userTimezone)}
                            </div>
                            <div className={styles.worklogFooter}>
                                <div className={styles.projectTaskInfo}>
                                    {draggedWorklog.project?.name && (
                                        <span>
                                            {draggedWorklog.project.name}
                                            {draggedWorklog.task && ` > ${typeof draggedWorklog.task === 'object' ? (draggedWorklog.task.name || 'Task') : draggedWorklog.task}`}
                                        </span>
                                    )}
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Resize preview */}
                    {isResizing && resizeWorklog && isSameDay(resizeWorklog.date, currentDate) && (
                        <div
                            className={`${styles.worklog} ${styles.resizePreview}`}
                            style={getResizePreviewStyle()}
                        >
                            <WorklogDescription
                                description={resizeWorklog.title}
                                className={styles.worklogTitle}
                                mentionableUsers={mentionableUsers}
                            />
                            <div className={styles.worklogTime}>
                                {formatTime12Hour(resizeWorklog.startTime, userTimezone)} - {formatTime12Hour(resizeWorklog.endTime, userTimezone)}
                            </div>
                        </div>
                    )}

                    {/* Current Time Indicator */}
                    {shouldShowCurrentTimeIndicator() && (
                        <div
                            ref={currentTimeLineRef}
                            className={styles.currentTimeLine}
                        />
                    )}
                </div>
            </div>

            {/* Add Worklog Modal */}
            <CalendarWorklogModal
                isOpen={showModal}
                onClose={() => {
                    setShowModal(false)
                    setPendingWorklog(null)
                    setIsEditMode(false)
                }}
                onSave={(worklogData) => {
                    if (isEditMode) {
                        onWorklogUpdate(worklogData.id, worklogData)
                    } else {
                        onWorklogCreate(worklogData)
                    }
                    setPendingWorklog(null)
                    setIsEditMode(false)
                }}
                onDelete={(worklogId) => {
                    onWorklogDelete(worklogId)
                    setPendingWorklog(null)
                    setIsEditMode(false)
                }}
                initialData={pendingWorklog}
                isEditMode={isEditMode}
                teammates={teammates}
                isAdmin={isAdmin}
                currentUser={currentUser}
                availableProjects={availableProjects}
                availableTasks={availableTasks}
                apiToken={apiToken}
            />

            {/* Context Menu */}
            <WorklogContextMenu
                isVisible={contextMenu.isVisible}
                position={contextMenu.position}
                onClose={closeContextMenu}
                onDuplicate={handleDuplicate}
                onDuplicateForMe={handleDuplicateForMe}
                onSplit={handleSplitFromMenu}
                onDelete={handleDeleteFromMenu}
                worklog={contextMenu.worklog}
            />
        </div>
    )
}
